import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Trash2, HardDriveDownload, Upload, CheckCircle, AlertTriangle, Zap, MessageSquare, ClipboardList, Bell, Loader2, MessageCircle } from 'lucide-react';
import type { Platform } from '../../../types/platform';
import { apiService } from '../../../services/api';
import { workerApi, exportPageData, importPageData } from '../../../services/workerApi';
import { createPortal } from 'react-dom';
import { ZaloQRModal } from '../../account/ZaloQRModal';

interface SettingsTabProps {
  account: Platform;
  onUpdated: (updated: Platform) => void;
  onDeleted: () => void;
}

const Section = ({ icon, title, danger, children }: { icon: React.ReactNode; title: string; danger?: boolean; children: React.ReactNode }) => (
  <div className={`bg-white rounded-xl border overflow-hidden ${danger ? 'border-red-100' : 'border-gray-100'}`}>
    <div className={`flex items-center gap-2 px-5 py-3.5 border-b ${danger ? 'border-red-50 bg-red-50/50' : 'border-gray-50 bg-gray-50/50'}`}>
      <span className={danger ? 'text-red-400' : 'text-gray-400'}>{icon}</span>
      <h3 className={`text-sm font-semibold ${danger ? 'text-red-700' : 'text-gray-700'}`}>{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const ConfirmDeleteModal = ({
  name,
  onConfirm,
  onCancel,
  deleting,
}: {
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}) => {
  const { t } = useTranslation('chatbot');
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Trash2 size={18} className="text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">{t('deleteConfirmTitle')}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{t('deleteConfirmSubtitle')}</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-3 mb-5">
          {t('deleteConfirmPre')} <span className="font-semibold text-gray-800">"{name}"</span> {t('deleteConfirmPost')}<br />
          <span className="text-red-600 font-medium">{t('deleteConfirmWarning')}</span>
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-60 transition-colors"
          >
            {deleting ? (
              <><RefreshCw size={13} className="animate-spin" /> {t('deleting')}</>
            ) : (
              <><Trash2 size={13} /> {t('deleteChatbot')}</>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const SettingsTab: React.FC<SettingsTabProps> = ({ account, onUpdated, onDeleted }) => {
  const { t } = useTranslation('chatbot');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [modeChanging, setModeChanging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showZaloQRModal, setShowZaloQRModal] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    stats?: Record<string, number>;
    error?: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // ── Bot state loaded directly from worker (source of truth) ────────────
  const isBrowser = account.platform.endsWith('_browser');
  const [botEnabled, setBotEnabled] = useState<boolean>(
    isBrowser ? account.polling_enabled : account.status === 'active'
  );
  const [chatbotMode, setChatbotMode] = useState<'basic' | 'expert'>(account.chatbot_mode ?? 'basic');
  const [configLoading, setConfigLoading] = useState(!isBrowser);

  // ── Follow-up reminder config ───────────────────────────────────────
  type FollowupCfg = {
    enabled: boolean;
    delay_hours: number;
    max_attempts: number;
    interval_hours: number;
    target_priorities: number[];
    mode: 'ai_generated' | 'template';
    template: string;
    message_tag: string;
  };
  const DEFAULT_FOLLOWUP: FollowupCfg = {
    enabled: false,
    delay_hours: 4,
    max_attempts: 2,
    interval_hours: 24,
    target_priorities: [1],
    mode: 'ai_generated',
    template: 'Bạn cần hỗ trợ thêm gì không ạ? 😊',
    message_tag: 'POST_PURCHASE_UPDATE',
  };
  // Mapping for the 5 priority states emitted by interest_analyzer (5 = hottest)
  const PRIORITY_OPTIONS: { value: number; label: string; hint: string }[] = [
    { value: 1, label: 'Thờ ơ', hint: 'Khách không phản hồi / chỉ xem' },
    { value: 2, label: 'Mới tìm hiểu', hint: 'Hỏi chung chung, chưa rõ nhu cầu' },
    { value: 3, label: 'Đang cân nhắc', hint: 'So sánh / cần thêm thông tin' },
    { value: 4, label: 'Rất quan tâm', hint: 'Hỏi giá / muốn tư vấn / muốn demo' },
    { value: 5, label: 'Cực kỳ nóng', hint: 'Sắp chốt / đã đồng ý' },
  ];
  const [followup, setFollowup] = useState<FollowupCfg>(DEFAULT_FOLLOWUP);
  const [followupSaving, setFollowupSaving] = useState(false);
  const [followupSaved, setFollowupSaved] = useState(false);

  useEffect(() => {
    if (isBrowser) {
      // Browser accounts: bot_enabled = polling_enabled (from channel doc).
      // Still fetch bot_config to load followup_config saved for this account.
      setBotEnabled(account.polling_enabled);
      let cancelled = false;
      workerApi.getBotConfig(account.platform_id)
        .then((cfg: any) => {
          if (cancelled) return;
          if (cfg.followup_config) {
            setFollowup({ ...DEFAULT_FOLLOWUP, ...cfg.followup_config });
          }
        })
        .catch(() => { /* worker unreachable — keep defaults */ });
      return () => { cancelled = true; };
    }
    let cancelled = false;
    setConfigLoading(true);
    workerApi.getBotConfig(account.platform_id)
      .then((cfg: any) => {
        if (cancelled) return;
        // bot_enabled is mapped to platform.is_active in the new backend
        setBotEnabled(cfg.platform?.is_active !== false);
        setChatbotMode(cfg.chatbot_mode === 'expert' ? 'expert' : 'basic');
        if (cfg.followup_config) {
          setFollowup({ ...DEFAULT_FOLLOWUP, ...cfg.followup_config });
        }
      })
      .catch(() => {
        // worker unreachable — fall back to backend-stored values
      })
      .finally(() => { if (!cancelled) setConfigLoading(false); });
    return () => { cancelled = true; };
  }, [account.platform_id, account.polling_enabled, isBrowser]);

  // ── Toggle chatbot on/off — writes directly to worker ───────────────
  const handleToggleStatus = async () => {
    if (toggling || configLoading) return;
    const newEnabled = !botEnabled;
    setToggling(true);
    try {
      // PatchBotConfig in the backend will upsert the local database and handle marketing APIs
      await workerApi.patchBotConfig(account.platform_id, { 
        bot_enabled: newEnabled,
        is_active: newEnabled 
      });
      
      setBotEnabled(newEnabled);
      // Mirror to parent so sidebar badge stays in sync
      onUpdated({
        ...account,
        status: newEnabled ? 'active' : 'inactive',
        polling_enabled: isBrowser ? newEnabled : account.polling_enabled,
      });
    } catch (e) {
      console.error('Failed to toggle bot status:', e);
    }
    setToggling(false);
  };

  // ── Set chatbot mode (basic / expert) — writes directly to worker ───
  const handleSetMode = async (mode: 'basic' | 'expert') => {
    if (modeChanging || configLoading || chatbotMode === mode) return;
    setModeChanging(true);
    try {
      await workerApi.patchBotConfig(account.platform_id, { chatbot_mode: mode });
      setChatbotMode(mode);
      onUpdated({ ...account, chatbot_mode: mode });
    } catch (e) {
      console.error('Failed to set chatbot_mode:', e);
    }
    setModeChanging(false);
  };

  // ── Save follow-up config ─────────────────────────────────────────────
  const handleSaveFollowup = async () => {
    if (followupSaving || configLoading) return;
    setFollowupSaving(true);
    setFollowupSaved(false);
    try {
      await workerApi.patchBotConfig(account.platform_id, { followup_config: followup });
      setFollowupSaved(true);
      setTimeout(() => setFollowupSaved(false), 2500);
    } catch (e) {
      console.error('Failed to save follow-up config:', e);
    }
    setFollowupSaving(false);
  };

  const updateFollowup = <K extends keyof FollowupCfg>(key: K, value: FollowupCfg[K]) => {
    setFollowup(prev => ({ ...prev, [key]: value }));
    setFollowupSaved(false);
  };
  // ── Export (Backup) ──────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await exportPageData(account.platform_id);
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${account.name.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
    setExporting(false);
  };

  // ── Import (Restore) ─────────────────────────────────────────────────
  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text) as Record<string, any>;
      const res = await importPageData(data);
      setImportResult({ success: true, stats: res.stats });
    } catch (err) {
      setImportResult({ success: false, error: String(err) });
    }
    setImporting(false);
    e.target.value = '';
  };

  // ── Delete ───────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Unified deletion endpoint (backend)
      await apiService.deletePlatform(account.id);

      // Worker cleanup: cascading delete of all chatbot data
      try {
        await workerApi.deletePlatform(account.id);
      } catch (workerErr) {
        console.warn('Worker cleanup failed (non-fatal):', workerErr);
      }
    } catch (e) {
      console.error('Failed to delete platform:', e);
      setDeleting(false);
      setShowDeleteModal(false);
      return;
    }
    setDeleting(false);
    setShowDeleteModal(false);
    onDeleted();
  };

  return (
    <div className="space-y-4">

      {/* Chatbot On/Off */}
      <Section icon={<RefreshCw size={14} />} title={t('sectionBotStatus')}>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-700">
              {botEnabled ? t('overviewChatbotOn') : t('overviewChatbotOff')}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {botEnabled
                ? t('botEnabledDesc')
                : t('botDisabledDesc')}
            </p>
          </div>
          <button
            type="button"
            title={toggling ? '' : (botEnabled ? t('disableBot') : t('enableBot'))}
            onClick={handleToggleStatus}
            disabled={toggling || configLoading}
            className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 disabled:cursor-not-allowed ${toggling || configLoading ? 'bg-gray-300' : botEnabled ? 'bg-[#3390ec]' : 'bg-gray-200'
              }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${botEnabled && !toggling && !configLoading ? 'translate-x-5' : 'translate-x-0'
                }`}
            />
          </button>
        </div>
      </Section>

      {/* Zalo QR Login for browser mode */}
      {isBrowser && account.platform === 'zalo_browser' && (
        <Section icon={<MessageCircle size={14} />} title="Cấu hình Zalo">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Đăng nhập Zalo Web</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Quét mã QR để liên kết tài khoản Zalo với chatbot (bắt buộc để gửi/nhận tin).
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowZaloQRModal(true)}
              className="px-4 py-2 bg-[#0068ff] text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Quét mã QR
            </button>
          </div>
          {showZaloQRModal && (
            <ZaloQRModal
              accountId={account.platform_id}
              accountName={account.name}
              onSuccess={() => {
                setShowZaloQRModal(false);
                onUpdated(account);
              }}
              onClose={() => setShowZaloQRModal(false)}
            />
          )}
        </Section>
      )}

      {/* Chatbot Mode */}
      <Section icon={<Zap size={14} />} title={t('modeSection')}>
        <p className="text-xs text-gray-400 mb-3">
          Chế độ <span className="font-medium text-gray-600">Hỗ Trợ Cơ Bản</span> trả lời nhanh, tiết kiệm tài nguyên.
          Chế độ <span className="font-medium text-gray-600">Chuyên Viên Bán Hàng</span> phân tích sâu hơn, tư vấn chủ động — nhưng tốn nhiều tài nguyên AI hơn.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {/* Basic mode card */}
          <button
            type="button"
            disabled={modeChanging || configLoading}
            onClick={() => handleSetMode('basic')}
            className={`relative flex flex-col gap-1.5 rounded-xl border-2 p-3 text-left transition-all duration-150 disabled:opacity-60 ${chatbotMode === 'basic'
              ? 'border-[#3390ec] bg-blue-50 shadow-sm'
              : 'border-gray-100 hover:border-gray-300 bg-white'
              }`}
          >
            <div className="flex items-center gap-1.5">
              <MessageSquare size={14} className="text-[#3390ec]" />
              <span className="text-xs font-semibold text-gray-700">{t('modeBasic')}</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">{t('modeBasicDesc')}</p>
            {chatbotMode === 'basic' && (
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#3390ec]" />
            )}
          </button>

          {/* Expert mode card */}
          <button
            type="button"
            disabled={modeChanging || configLoading}
            onClick={() => handleSetMode('expert')}
            className={`relative flex flex-col gap-1.5 rounded-xl border-2 p-3 text-left transition-all duration-150 disabled:opacity-60 ${chatbotMode === 'expert'
              ? 'border-amber-400 bg-amber-50 shadow-sm'
              : 'border-gray-100 hover:border-gray-300 bg-white'
              }`}
          >
            <div className="flex items-center gap-1.5">
              <Zap size={14} className="text-amber-500" />
              <span className="text-xs font-semibold text-gray-700">{t('modeExpert')}</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">{t('modeExpertDesc')}</p>
            {chatbotMode === 'expert' && (
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-amber-400" />
            )}
          </button>
        </div>
      </Section>

      {/* Follow-up Reminder */}
      {!isBrowser && (
        <Section icon={<Bell size={14} />} title={t('followupSection')}>
          <p className="text-xs text-gray-400 mb-4">
            Tự động gửi tin nhắn nhắc lại nếu khách hàng không phản hồi sau một khoảng thời gian.
            Hệ thống sẽ tự dừng nếu khách đã trả lời.
          </p>

          {/* Toggle enable */}
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-700">
                {followup.enabled ? t('followupOn') : t('followupOff')}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {t('followupToggleDesc')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => updateFollowup('enabled', !followup.enabled)}
              disabled={configLoading}
              className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 disabled:cursor-not-allowed ${followup.enabled ? 'bg-[#3390ec]' : 'bg-gray-200'
                }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${followup.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
              />
            </button>
          </div>

          {/* Browser accounts: polling must be running for followup to work */}
          {isBrowser && (
            <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 mb-4 text-xs ${account.polling_enabled
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-amber-50 border border-amber-200 text-amber-700'
              }`}>
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <span>
                {account.polling_enabled
                  ? 'Phiên polling đang chạy — tính năng nhắc lại hoạt động bình thường.'
                  : <>
                    <strong>Polling chưa bật.</strong> Tính năng nhắc lại cần phiên polling đang hoạt động để gửi tin qua browser. Hãy bật polling ở tab <strong>Cài đặt → Polling</strong> trước khi kích hoạt nhắc lại.
                  </>
                }
              </span>
            </div>
          )}

          {followup.enabled && (
            <div className="space-y-4 pt-3 border-t border-gray-100">
              {/* Target priority states */}
              <div>
                <span className="text-xs text-gray-500 block mb-2">
                  Chỉ nhắc lại khi khách đang ở các trạng thái sau
                </span>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {PRIORITY_OPTIONS.map(opt => {
                    const checked = followup.target_priorities.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-start gap-2 rounded-lg border-2 p-2 text-left text-xs cursor-pointer transition-all ${checked
                          ? 'border-[#3390ec] bg-blue-50'
                          : 'border-gray-100 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 accent-[#3390ec]"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? Array.from(new Set([...followup.target_priorities, opt.value])).sort()
                              : followup.target_priorities.filter(v => v !== opt.value);
                            updateFollowup('target_priorities', next);
                          }}
                        />
                        <span className="min-w-0">
                          <span className="font-semibold text-gray-700 block">P{opt.value} — {opt.label}</span>
                          <span className="text-[11px] text-gray-400 block">{opt.hint}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {followup.target_priorities.length === 0 && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    ⚠ Chưa chọn trạng thái nào — sẽ không có cuộc trò chuyện nào được nhắc.
                  </p>
                )}
              </div>

              {/* Timing */}
              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs text-gray-500">Gửi nhắc sau (giờ)</span>
                  <input
                    type="number"
                    min={0.1}
                    step={0.5}
                    value={followup.delay_hours}
                    onChange={(e) => updateFollowup('delay_hours', parseFloat(e.target.value) || 0)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-[#3390ec] focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500">Số lần nhắc tối đa</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    step={1}
                    value={followup.max_attempts}
                    onChange={(e) => updateFollowup('max_attempts', parseInt(e.target.value) || 1)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-[#3390ec] focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500">Khoảng cách (giờ)</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={followup.interval_hours}
                    onChange={(e) => updateFollowup('interval_hours', parseFloat(e.target.value) || 1)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-[#3390ec] focus:outline-none"
                  />
                </label>
              </div>

              {/* Mode */}
              <div>
                <span className="text-xs text-gray-500 block mb-2">Nội dung tin nhắn nhắc</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => updateFollowup('mode', 'ai_generated')}
                    className={`rounded-lg border-2 p-2.5 text-left text-xs transition-all ${followup.mode === 'ai_generated'
                      ? 'border-[#3390ec] bg-blue-50'
                      : 'border-gray-100 hover:border-gray-300'
                      }`}
                  >
                    <p className="font-semibold text-gray-700">AI tự sinh</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Theo ngữ cảnh hội thoại</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => updateFollowup('mode', 'template')}
                    className={`rounded-lg border-2 p-2.5 text-left text-xs transition-all ${followup.mode === 'template'
                      ? 'border-[#3390ec] bg-blue-50'
                      : 'border-gray-100 hover:border-gray-300'
                      }`}
                  >
                    <p className="font-semibold text-gray-700">Mẫu cố định</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Tin nhắn soạn sẵn</p>
                  </button>
                </div>
              </div>

              {/* Template editor (always visible — used as fallback when AI fails) */}
              <label className="block">
                <span className="text-xs text-gray-500">
                  {followup.mode === 'template' ? 'Mẫu tin nhắn nhắc' : 'Mẫu dự phòng (khi AI không khả dụng)'}
                </span>
                <textarea
                  rows={2}
                  value={followup.template}
                  onChange={(e) => updateFollowup('template', e.target.value)}
                  placeholder="Bạn cần hỗ trợ thêm gì không ạ? 😊"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#3390ec] focus:outline-none resize-none"
                />
                <span className="text-[11px] text-gray-400">
                  Hỗ trợ biến: <code className="bg-gray-100 px-1 rounded">{'{customer_name}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{page_name}'}</code>
                </span>
              </label>

              {/* Message tag (advanced) */}
              <label className="block">
                <span className="text-xs text-gray-500">Message Tag (dùng khi ngoài 24h)</span>
                <select
                  value={followup.message_tag}
                  onChange={(e) => updateFollowup('message_tag', e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-[#3390ec] focus:outline-none"
                >
                  <option value="POST_PURCHASE_UPDATE">POST_PURCHASE_UPDATE — đơn hàng / giao hàng</option>
                  <option value="CONFIRMED_EVENT_UPDATE">CONFIRMED_EVENT_UPDATE — sự kiện đã xác nhận</option>
                  <option value="ACCOUNT_UPDATE">ACCOUNT_UPDATE — cập nhật tài khoản</option>
                </select>
                <span className="text-[11px] text-amber-600 mt-1 block">
                  ⚠ Facebook chỉ cho dùng tag đúng mục đích — không được dùng để gửi quảng cáo
                </span>
              </label>

              <div className="flex items-center justify-end gap-2 pt-2">
                {followupSaved && (
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <CheckCircle size={12} /> {t('followupSaved')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleSaveFollowup}
                  disabled={followupSaving || configLoading}
                  className="px-4 py-1.5 bg-[#3390ec] text-white rounded-lg text-sm font-medium hover:bg-[#2b7dd6] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {followupSaving ? t('saving') : t('saveConfig')}
                </button>
              </div>
            </div>
          )}

          {!followup.enabled && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSaveFollowup}
                disabled={followupSaving || configLoading}
                className="px-4 py-1.5 bg-[#3390ec] text-white rounded-lg text-sm font-medium hover:bg-[#2b7dd6] disabled:opacity-60"
              >
                {followupSaving ? t('saving') : t('saveConfig')}
              </button>
            </div>
          )}
        </Section>
      )}

      {/* Interview-based config */}
      <Section icon={<ClipboardList size={14} />} title={t('interviewSection')}>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-700">{t('interviewTitle')}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('interviewDesc')}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('../interview')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#3390ec] text-white rounded-lg text-sm font-medium hover:bg-[#2b7dd6] transition-colors whitespace-nowrap"
          >
            <ClipboardList size={13} />
            {t('startInterview')}
          </button>
        </div>
      </Section>

      {/* Backup & Restore */}
      <Section icon={<HardDriveDownload size={14} />} title={t('backupSection')}>
        <div className="space-y-4">
          <p className="text-xs text-gray-500 leading-relaxed">
            Xuất cấu hình AI, sản phẩm/dịch vụ và Knowledge Base của chatbot này
            thành file <span className="font-mono bg-gray-100 px-1 rounded">.json</span>.
            Dùng để backup hoặc chuyển cấu hình sang chatbot khác.
          </p>

          {/* Export */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-700">{t('exportTitle')}</p>
              <p className="text-xs text-gray-400 mt-0.5">{t('exportDesc')}</p>
            </div>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-[#3390ec] text-white rounded-lg text-sm font-medium hover:bg-[#2b7dd6] disabled:opacity-50 transition-colors"
            >
              {exporting
                ? <><RefreshCw size={13} className="animate-spin" /> {t('exporting')}</>
                : <><HardDriveDownload size={13} /> {t('exportBackup')}</>}
            </button>
          </div>

          {/* Import */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-medium text-gray-700">{t('importTitle')}</p>
              <p className="text-xs text-gray-400 mt-0.5">{t('importDesc')}</p>
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {importing
                ? <><RefreshCw size={13} className="animate-spin" /> {t('importing')}</>
                : <><Upload size={13} /> {t('chooseFile')}</>}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              aria-label="Chọn file backup JSON để nhập"
              className="hidden"
              onChange={handleImport}
            />
          </div>

          {/* Import result */}
          {importResult && (
            <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm ${importResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}>
              {importResult.success
                ? (
                  <>
                    <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>
                      {t('importSuccess')}
                      {importResult.stats && (
                        <span className="ml-1 opacity-80">
                          (cấu hình AI {importResult.stats.bot_config === 1 ? '✓' : '✗'} &middot; {importResult.stats.knowledge_base ?? 0} KB)
                        </span>
                      )}
                    </span>
                  </>
                ) : (
                  <><AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> {t('importFailed')} {importResult.error}</>
                )}
            </div>
          )}
        </div>
      </Section>

      {/* Danger Zone */}
      <Section icon={<Trash2 size={14} />} title={t('dangerZone')} danger>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">{t('deleteThisTitle')}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('deleteThisDesc', { name: account.name })}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
          >
            <Trash2 size={13} /> {t('deleteBtn')}
          </button>
        </div>
      </Section>

      {showDeleteModal && (
        <ConfirmDeleteModal
          name={account.name}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          deleting={deleting}
        />
      )}
    </div>
  );
};
